/**
 * Rack It Stack It mobile header + navigation drawer.
 *
 * Scoped entirely to the mobile-only elements added in sections/header.liquid
 * and snippets/rsi-header-mobile-nav.liquid. Does not touch the desktop nav
 * (snippets/rsi-header-custom-desktop-nav.liquid) or assets/rsi-desktop-header.js
 * in any way — this file only ever reads/writes classes scoped to the mobile
 * search row and the <main-menu> drawer, both already display:none at
 * >= 990px (assets/rsi-mobile-nav.css).
 */
(function () {
  if (window.__rsiMobileHeaderInit) return;
  window.__rsiMobileHeaderInit = true;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var headerEl = document.querySelector('.rsi-desktop-header');
    if (!headerEl) return;

    var mainMenuEl = headerEl.querySelector('main-menu');
    var disclosure = headerEl.querySelector('.main-menu__disclosure');
    var hamburgerToggle = headerEl.querySelector('.main-menu__toggle');
    var searchToggle = document.getElementById('rsi-mobile-search-toggle');
    var searchInput = document.getElementById('header-search');
    var mobileNav = headerEl.querySelector('.rsi-mobile-nav');

    /* ------------------------------------------------------------
       Drawer height: pure CSS now ("height: 100svh", assets/
       rsi-mobile-nav.css, ".rsi-desktop-header .main-menu__content").
       The old "--rsi-mobile-drawer-height" JS layer here was REMOVED
       because it was actively causing the drawer's bottom to be cut
       off: it measured visualViewport.height/innerHeight at drawer-open
       time, and if the drawer was opened while Safari's toolbar was
       collapsed that reading is the LARGE viewport — the var() line
       then overrode the safe 100svh value in CSS, sizing the drawer
       taller than the visible area, and its bottom (the pinned footer)
       disappeared behind the toolbar the moment it re-expanded. JS has
       no way to read the small-viewport height directly, so this can't
       be fixed by measuring differently — svh IS that value, natively,
       and never changes as the toolbar animates.
    ------------------------------------------------------------ */

    /* ------------------------------------------------------------
       Relocate the Warehouse Optimiser gate (rendered inside
       snippets/rsi-header-mobile-nav.liquid so it's present on every
       page — see that file's comment) out of the drawer's own DOM
       subtree, into <body>, before anything can interact with it.

       ".main-menu__content" (the drawer panel this gate is rendered
       inside) is "visibility: hidden" whenever the drawer is closed
       (assets/rsi-mobile-nav.css), and "visibility" is inherited by
       descendants with no own value — so the gate silently inherited
       "hidden" the moment the drawer that opened it finished closing.
       It also always carries a "transform" (translateX(-100%) closed,
       translateX(0) open), which per the CSS spec makes it the
       containing block for its "position: fixed" descendants — so the
       gate's own "inset: 0" was resolving against the 82vw drawer
       panel's box, not the true viewport. Moving it to be a direct
       child of <body> — a plain, unconstrained ancestor — before any
       click can reach it avoids both problems entirely.
    ------------------------------------------------------------ */
    var genericGate = mobileNav && mobileNav.querySelector('[data-rsi-wo-gate-generic]');
    if (genericGate && genericGate.parentElement !== document.body) {
      document.body.appendChild(genericGate);
    }

    /* ------------------------------------------------------------
       Fixed header + fixed announcement-strip offsets (see assets/
       rsi-mobile-nav.css, ".rsi-desktop-header.header" comment, for why
       "position: fixed" replaced "position: sticky" on both, and why
       BOTH are fixed rather than just the header).

       Two earlier attempts fixed this by tuning the header's OWN
       timing alone (first rAF-queuing the transform, then calling it
       synchronously from the scroll listener) while leaving the
       announcement strip (".shopify-section-group-header-group.
       cc-announcement", sections/announcement.liquid) in normal
       document flow. Both still showed a white gap under fast
       scrolling, because that architecture has two independently-timed
       positions that are SUPPOSED to always match: the strip's (exact,
       native scrolling, zero lag) and the header's (a JS approximation
       of the same boundary, however quickly it's computed). No amount
       of tuning the JS side alone closes a gap that only exists because
       something else, entirely outside that JS, is exactly on time.

       Fix: make the strip JS-positioned too, and derive both transforms
       from the exact same "progress" value in the same tick (below) —
       algebraically complementary, so the strip's bottom edge and the
       header's top edge land on the same pixel regardless of how stale
       "progress" is relative to true scroll position. There's no longer
       a second, independently-timed measurement for either to drift
       apart from.

       Stability (flicker/white-flash/jump during fast upward scrolling
       on iPhone Safari): only ever change "transform: translate3d(...)"
       during scroll — a compositor-only property — while "top: 0"
       (assets/rsi-mobile-nav.css) stays completely static on both
       elements, never touched by JS; changing "top" itself on every
       scroll frame is a layout property and forces a repaint from the
       layout tree, which is what produces an occasional blank/default
       frame on Safari. Reads (measuring the strip/header height) and
       writes (applying the transforms/spacer) are also fully separated:
       "measureDimensions()" only ever runs from a resize-class trigger
       (ResizeObserver/resize/breakpoint change/font load), never from
       the scroll handler, so a single scroll frame never does a layout
       read immediately followed by a write (the classic layout-
       thrashing pattern) — the scroll handler only ever reads the
       already-cached "announcementHeight" and writes two "transform"s.

       The scroll path deliberately does NOT queue through
       requestAnimationFrame (resize still does, below) — calling
       "applyHeaderOffset()" straight from the passive scroll listener
       applies both transforms as early as the browser will allow, no
       later than the same paint the native scroll itself lands in. This
       is still cheap to call at native scroll-event frequency: it reads
       only the already-cached "announcementHeight" (no layout read) and
       its own "progress === lastOffset" guard skips both DOM writes
       entirely once the rounded pixel value stops changing. */
    var headerWrap = headerEl.parentElement;
    if (headerWrap) {
      var mobileHeaderMq = window.matchMedia('(max-width: 989.98px)');
      var announcementWrap = document.querySelector('.shopify-section-group-header-group.cc-announcement');
      var announcementHeight = 0;
      var headerHeight = 0;
      var lastOffset = null;
      var lastSpacer = null;
      var resizeTicking = false;

      // Reads only — never called from the scroll handler.
      var measureDimensions = function () {
        announcementHeight = announcementWrap ? announcementWrap.getBoundingClientRect().height : 0;
        headerHeight = headerEl.getBoundingClientRect().height;
      };

      // Write only — the hot scroll path. Reads nothing from the DOM;
      // derives BOTH the header's and the announcement strip's transform
      // from one shared "progress" value (0 at the top, up to
      // announcementHeight once scrolled past the strip) so they can
      // never disagree with each other — see the CSS comment on
      // ".rsi-desktop-header.header" (assets/rsi-mobile-nav.css) for why
      // this replaced two independently-timed mechanisms. Clamped so iOS
      // overscroll (which can report scrollY < 0 or > max) can never push
      // "progress" outside [0, announcementHeight].
      var applyHeaderOffset = function () {
        if (!mobileHeaderMq.matches) {
          if (lastOffset !== null) {
            headerEl.style.transform = '';
            if (announcementWrap) announcementWrap.style.transform = '';
            lastOffset = null;
          }
          return;
        }
        var scrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
        var progress = Math.round(Math.max(0, Math.min(announcementHeight, scrollY)));
        if (progress === lastOffset) return;
        lastOffset = progress;

        var headerDown = announcementHeight - progress;
        headerEl.style.transform = headerDown === 0 ? '' : 'translate3d(0, ' + headerDown + 'px, 0)';

        if (announcementWrap) {
          announcementWrap.style.transform = progress === 0 ? '' : 'translate3d(0, -' + progress + 'px, 0)';
        }
      };

      // Both the announcement strip and the header are "position: fixed"
      // now (assets/rsi-mobile-nav.css), so neither reserves its own
      // space in normal flow any more — this single spacer (on the
      // header's own section wrapper, immediately after the announcement
      // section in the DOM) reserves BOTH heights together so the real
      // page content below starts in the right place.
      var applySpacer = function () {
        if (!mobileHeaderMq.matches) {
          if (lastSpacer !== null) {
            headerWrap.style.paddingTop = '';
            lastSpacer = null;
          }
          return;
        }
        var totalHeight = announcementHeight + headerHeight;
        if (totalHeight > 0 && totalHeight !== lastSpacer) {
          lastSpacer = totalHeight;
          headerWrap.style.paddingTop = totalHeight + 'px';
        }
      };

      // Resize-class refresh: re-measure, then re-apply both the
      // spacer and the current scroll offset against the fresh
      // numbers. rAF-coalesced so ResizeObserver firing mid-scroll
      // (e.g. Safari's URL bar hide/show on scroll subtly changes
      // "env(safe-area-inset-top)", which changes this header's own
      // rendered height) never runs synchronously inside a scroll
      // frame — it queues its own frame instead of fighting the
      // scroll handler's.
      var refresh = function () {
        if (resizeTicking) return;
        resizeTicking = true;
        window.requestAnimationFrame(function () {
          measureDimensions();
          applySpacer();
          lastOffset = null; // dimensions changed — force a re-write even if the pixel value repeats
          applyHeaderOffset();
          resizeTicking = false;
        });
      };

      refresh();

      window.addEventListener('scroll', applyHeaderOffset, { passive: true });

      if (window.ResizeObserver) {
        var sizeObserver = new ResizeObserver(refresh);
        sizeObserver.observe(headerEl);
        if (announcementWrap) sizeObserver.observe(announcementWrap);
      } else {
        window.addEventListener('resize', refresh);
      }

      if (typeof mobileHeaderMq.addEventListener === 'function') {
        mobileHeaderMq.addEventListener('change', refresh);
      } else if (typeof mobileHeaderMq.addListener === 'function') {
        mobileHeaderMq.addListener(refresh);
      }

      window.addEventListener('orientationchange', refresh);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(refresh);
      }
    }

    /* ------------------------------------------------------------
       Expandable search row: open/close, focus, Escape, mutual
       exclusion with the drawer.
    ------------------------------------------------------------ */
    function isSearchOpen() {
      return headerEl.classList.contains('rsi-mobile-search-is-open');
    }

    function closeDrawerIfOpen() {
      if (disclosure && hamburgerToggle && disclosure.classList.contains('is-open')) {
        hamburgerToggle.click();
      }
    }

    function openSearch() {
      if (isSearchOpen() || !searchToggle) return;
      closeDrawerIfOpen();
      headerEl.classList.add('rsi-mobile-search-is-open');
      searchToggle.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(function () {
        if (searchInput) searchInput.focus();
      });
    }

    function closeSearch(restoreFocus) {
      if (!isSearchOpen() || !searchToggle) return;
      headerEl.classList.remove('rsi-mobile-search-is-open');
      searchToggle.setAttribute('aria-expanded', 'false');

      // predictive-search (assets/predictive-search.js) renders its own
      // full-viewport ".js-search-overlay" as soon as it has a query/
      // results — a fixed-position element, so it isn't clipped by this
      // row collapsing via max-height/overflow like everything else in
      // it. Without this, typing something, then collapsing the row,
      // left that dark overlay stuck covering the whole page with
      // nothing left visibly open to explain it. Calling the component's
      // own .close() (idempotent — safe even if it was never opened)
      // tears down its overlay/state the same way it would if the user
      // had triggered it directly.
      var searchEl = searchInput && searchInput.closest('predictive-search');
      if (searchEl && typeof searchEl.close === 'function') {
        searchEl.close();
      }

      if (restoreFocus) searchToggle.focus();
    }

    if (searchToggle) {
      searchToggle.addEventListener('click', function () {
        if (isSearchOpen()) {
          closeSearch(true);
        } else {
          openSearch();
        }
      });

      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && isSearchOpen()) {
          closeSearch(true);
        }
      });
    }

    if (hamburgerToggle) {
      // Native handling (assets/main.js, MainMenu#handleMainMenuToggle)
      // opens/closes the drawer itself; this just adds the one extra
      // side effect the spec asks for on top of it.
      hamburgerToggle.addEventListener('click', function () {
        closeSearch(false);
      });
    }

    // The Warehouse Optimiser gate modal (assets/rsi-warehouse-optimiser-
    // gate.js, triggered via [data-rsi-wo-gate-trigger] on the drawer's
    // "Plan Your Warehouse" CTA and Services link) is "position: fixed;
    // z-index: 9999" — far below this drawer's own z-index, so it would
    // render invisibly behind an open drawer. Closing the drawer first
    // (same DOM-bubbling-order trick as the search/drawer mutual
    // exclusion above: this listener is on an ancestor of the trigger,
    // so it always runs before the gate's own "document" click listener
    // opens the modal) keeps this a clean handoff — no double-open UI.
    if (mobileNav) {
      mobileNav.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-rsi-wo-gate-trigger]')) {
          closeDrawerIfOpen();
        }
      });
    }

    /* ------------------------------------------------------------
       Drawer backdrop + Escape/click-outside-to-close.

       MainMenu (assets/main.js) already implements all of this via
       .toggleOverlay() — dark ".js-overlay" backdrop, click-to-close,
       Escape-to-close while focus is inside the nav — but only ever
       calls it for *nested* ".js-toggle"/".js-back" disclosures, never
       for the top-level hamburger disclosure itself (harmless while the
       drawer was 100vw wide with nothing left to dim; not harmless now
       that it's ~82vw with real page/overlay visible beside it). A
       MutationObserver mirrors the disclosure's own "is-open" class
       (the single source of truth MainMenu already maintains) onto
       .toggleOverlay(), rather than guessing at click-handler timing.
    ------------------------------------------------------------ */
    if (disclosure && mainMenuEl && typeof mainMenuEl.toggleOverlay === 'function') {
      var wasOpen = disclosure.classList.contains('is-open');

      var syncOverlay = function () {
        var isOpen = disclosure.classList.contains('is-open');
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        mainMenuEl.toggleOverlay(isOpen);

        // "position: sticky" on .rsi-desktop-header (assets/rsi-mobile-nav.css)
        // creates a stacking context on the header, which traps this drawer's
        // own z-index (it's a DOM descendant of the header) below anything
        // outside it — including the shared ".js-overlay" backdrop this same
        // toggleOverlay() call just showed. Promoting the header's own
        // z-index only for the window the drawer is actually open lets the
        // drawer/overlay escape and cover the full screen as intended,
        // without permanently outranking real overlays like the cart/
        // compare drawers (assets/main.css, ".drawer { z-index: 40; }")
        // the rest of the time.
        headerEl.classList.toggle('rsi-mobile-drawer-open', isOpen);

        if (isOpen) {
          closeSearch(false);

          // assets/main.js's own trapFocus(), called from MainMenu.open()
          // for this top-level disclosure, tries to focus the <details>
          // element itself — a silent no-op, since plain <details> isn't
          // natively focusable without a tabindex. Real focus never
          // actually leaves the hamburger <summary>, which is also why
          // Escape never closed the drawer: the existing native
          // Escape-to-close listener (assets/main.js, toggleOverlay())
          // is bound to ".main-nav", so it only ever receives the keyup
          // if focus is inside it. Moving focus to the first drawer row
          // satisfies the "focus trapped" requirement and is what makes
          // that already-wired Escape handling actually fire.
          var firstFocusable = mobileNav && mobileNav.querySelector('[data-rsi-mobile-panels] [data-open-panel]');
          if (firstFocusable) {
            window.requestAnimationFrame(function () {
              firstFocusable.focus();
            });
          }
        } else {
          if (mobileNav && typeof mobileNav._rsiResetPanels === 'function') {
            mobileNav._rsiResetPanels();
          }
          // "Restored": return focus to the hamburger regardless of which
          // path closed the drawer (X button, overlay click, Escape).
          if (hamburgerToggle) hamburgerToggle.focus();
        }
      };

      new MutationObserver(syncOverlay).observe(disclosure, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    /* ------------------------------------------------------------
       Drill-down panels (Products / Business / Home & Garage /
       Services, plus the nested Racking Systems panel under
       Business). Fully custom markup (data-open-panel/data-back),
       deliberately not using the native ".js-toggle"/".js-back"
       handling in assets/main.js's MainMenu.handleNavClick, which is
       built around <details> disclosures rather than a panel stack.
    ------------------------------------------------------------ */
    if (mobileNav) {
      var panelsWrapper = mobileNav.querySelector('[data-rsi-mobile-panels]');

      if (panelsWrapper) {
        var panels = Array.prototype.slice.call(
          panelsWrapper.querySelectorAll('.rsi-mobile-nav__panel')
        );

        var activatePanel = function (name) {
          panels.forEach(function (panel) {
            var isNewlyActive = panel.getAttribute('data-panel') === name;
            panel.classList.toggle('is-active', isNewlyActive);

            // Reset scroll to top exactly once, only on the transition INTO
            // a panel (never while it's already open/showing — this
            // function only ever runs from an actual open/back click, not
            // from scroll/resize/focus, so there's no risk of this fighting
            // the user's own in-progress scrolling the way a resize- or
            // focus-driven reset would). The panel stays in the DOM across
            // drawer close/reopen rather than being re-rendered, so without
            // this a panel scrolled down once would silently reopen already
            // scrolled down every time after.
            if (isNewlyActive) {
              var body = panel.querySelector('.rsi-mobile-nav__panel-body') || panel;
              body.scrollTop = 0;
            }
          });

          // Mirrors the CSS ":has(.rsi-mobile-nav__panel--scrollable.is-active)"
          // selector (assets/rsi-mobile-nav.css) via a plain class instead —
          // ":has()" needs a fairly recent browser (Safari 15.4+/Chrome
          // 105+/Firefox 121+); this class-based toggle gives the exact same
          // "give .rsi-mobile-nav a real height only while a long panel is
          // open" behaviour on any browser regardless of ":has()" support,
          // which is what actually makes that panel's internal scroll fix
          // reliable on real (potentially older) mobile Safari rather than
          // only in modern desktop testing. Reads the newly active panel's
          // own "--scrollable" modifier class instead of a hardcoded panel
          // name, so any panel can opt in without a JS change.
          var activePanelEl = panelsWrapper.querySelector('.rsi-mobile-nav__panel[data-panel="' + name + '"]');
          var isScrollablePanel = !!(activePanelEl && activePanelEl.classList.contains('rsi-mobile-nav__panel--scrollable'));
          mobileNav.classList.toggle('rsi-mobile-nav--panel-scrollable', isScrollablePanel);

          panelsWrapper.querySelectorAll('[data-open-panel]').forEach(function (btn) {
            btn.setAttribute('aria-expanded', btn.getAttribute('data-open-panel') === name ? 'true' : 'false');
          });

          if (name !== 'root') {
            var activePanel = panelsWrapper.querySelector('.rsi-mobile-nav__panel[data-panel="' + name + '"]');
            var focusTarget = activePanel && activePanel.querySelector('[data-back]');
            if (focusTarget) {
              window.requestAnimationFrame(function () {
                focusTarget.focus();
              });
            }
          }
        };

        panelsWrapper.addEventListener('click', function (evt) {
          var openBtn = evt.target.closest('[data-open-panel]');
          if (openBtn) {
            activatePanel(openBtn.getAttribute('data-open-panel'));
            return;
          }

          var backBtn = evt.target.closest('[data-back]');
          if (backBtn) {
            var currentPanel = backBtn.closest('.rsi-mobile-nav__panel');
            var parent = currentPanel && currentPanel.getAttribute('data-parent');
            activatePanel(parent || 'root');
          }
        });

        mobileNav._rsiResetPanels = function () {
          activatePanel('root');
        };
      }

      /* ------------------------------------------------------------
         VAT: a local proxy toggle in the drawer footer, forwarding to
         (and syncing from) whichever real toggle the VAT & Tax Switcher
         app embed renders elsewhere on the page (config/settings_data.json,
         "vat-tax-switcher"). Exact same technique as
         assets/rsi-pdp-vat-sync.js's PDP toggle — this file does not
         reimplement VAT math, it only forwards interaction to the
         app's own (already-working) toggle(s). Stays hidden until a
         real toggle is actually found, so it can never ship as a fake,
         disconnected control if the app embed is removed or fails to
         render on a given page.
      ------------------------------------------------------------ */
      var vatBlock = mobileNav.querySelector('[data-rsi-mobile-vat]');
      var localVatToggle = vatBlock && vatBlock.querySelector('.toggle-checkbox');

      if (vatBlock && localVatToggle) {
        var exOption = vatBlock.querySelector('[data-rsi-mobile-vat-option="ex"]');
        var incOption = vatBlock.querySelector('[data-rsi-mobile-vat-option="inc"]');
        var isForwardingVat = false;

        var getRealVatToggles = function () {
          return Array.prototype.filter.call(
            document.querySelectorAll('.tax-switcher .toggle-checkbox'),
            function (el) { return el !== localVatToggle; }
          );
        };

        var updateVatLabels = function () {
          if (exOption) exOption.setAttribute('data-active', localVatToggle.checked ? 'false' : 'true');
          if (incOption) incOption.setAttribute('data-active', localVatToggle.checked ? 'true' : 'false');
        };

        var syncVatFromReal = function () {
          if (isForwardingVat) return;
          var real = getRealVatToggles()[0];
          if (!real) return;

          if (vatBlock.hidden) vatBlock.hidden = false;

          if (localVatToggle.checked !== real.checked) {
            localVatToggle.checked = real.checked;
            updateVatLabels();
          }
        };

        localVatToggle.addEventListener('change', function () {
          var reals = getRealVatToggles();
          isForwardingVat = true;
          reals.forEach(function (real) {
            if (real.checked !== localVatToggle.checked) real.click();
          });
          isForwardingVat = false;
          updateVatLabels();
        });

        updateVatLabels();
        syncVatFromReal();
        window.setInterval(syncVatFromReal, 400);
      }
    }
  });
})();
